import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Dev-only on-page SEO + structured-data validator.
 *
 * Mounts in dev (`import.meta.env.DEV`) or whenever `?seo=1` is present in the URL.
 * Reads the live <head> after react-helmet-async settles, runs a battery of
 * presence/length/format checks, and shows a pass/fail report card in the
 * bottom-right corner of the viewport. Click to expand.
 *
 * Intentionally side-effect-free: never mutates DOM, never blocks paint, never
 * ships in a normal production build (root render gates it).
 */

type Severity = "pass" | "warn" | "fail";
type Check = { id: string; label: string; severity: Severity; detail?: string };

const SCAN_DELAY_MS = 600;

function getMeta(name: string): string | null {
  const el =
    document.querySelector(`meta[name="${name}"]`) ||
    document.querySelector(`meta[property="${name}"]`);
  return el?.getAttribute("content") ?? null;
}

function getLink(rel: string): string | null {
  return document.querySelector(`link[rel="${rel}"]`)?.getAttribute("href") ?? null;
}

function getJsonLd(): { node: HTMLScriptElement; data: unknown; error?: string }[] {
  const nodes = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'));
  return nodes.map((node) => {
    try {
      return { node, data: JSON.parse(node.textContent ?? "null") };
    } catch (e) {
      return { node, data: null, error: (e as Error).message };
    }
  });
}

function check(label: string, severity: Severity, detail?: string, id?: string): Check {
  return { id: id ?? label, label, severity, detail };
}

function run(): Check[] {
  const out: Check[] = [];
  const title = document.title?.trim() ?? "";
  if (!title) out.push(check("title present", "fail"));
  else if (title.length < 20) out.push(check("title length", "warn", `${title.length} chars (recommend 30–60)`));
  else if (title.length > 65) out.push(check("title length", "warn", `${title.length} chars (recommend ≤60)`));
  else out.push(check("title", "pass", title));

  const desc = getMeta("description");
  if (!desc) out.push(check("meta description", "fail"));
  else if (desc.length < 50) out.push(check("description length", "warn", `${desc.length} chars (recommend 80–160)`));
  else if (desc.length > 165) out.push(check("description length", "warn", `${desc.length} chars (recommend ≤160)`));
  else out.push(check("meta description", "pass", `${desc.length} chars`));

  const canonical = getLink("canonical");
  if (!canonical) out.push(check("canonical link", "fail"));
  else if (!/^https?:\/\//.test(canonical)) out.push(check("canonical absolute URL", "fail", canonical));
  else out.push(check("canonical", "pass", canonical));

  const robots = getMeta("robots") ?? "";
  out.push(
    /noindex/.test(robots)
      ? check("robots", "warn", `noindex set: ${robots}`)
      : check("robots indexable", "pass", robots || "default"),
  );

  // Open Graph
  const ogChecks: [string, Severity][] = [
    ["og:title", "fail"],
    ["og:description", "fail"],
    ["og:url", "fail"],
    ["og:image", "fail"],
    ["og:type", "warn"],
    ["og:site_name", "warn"],
    ["og:locale", "warn"],
  ];
  for (const [k, missingSeverity] of ogChecks) {
    const v = getMeta(k);
    out.push(v ? check(k, "pass", v) : check(k, missingSeverity, "missing"));
  }

  // Twitter
  for (const k of ["twitter:card", "twitter:title", "twitter:description", "twitter:image"] as const) {
    const v = getMeta(k);
    out.push(v ? check(k, "pass") : check(k, k === "twitter:card" ? "fail" : "warn", "missing"));
  }

  // hreflang
  const hreflangs = Array.from(document.querySelectorAll('link[rel="alternate"][hreflang]'));
  out.push(
    hreflangs.length
      ? check("hreflang alternates", "pass", `${hreflangs.length} declared`)
      : check("hreflang alternates", "warn", "none declared"),
  );

  // Geo
  const geoRegion = getMeta("geo.region");
  out.push(geoRegion ? check("geo.region", "pass", geoRegion) : check("geo.region", "warn", "missing"));

  // JSON-LD
  const jsonLd = getJsonLd();
  if (!jsonLd.length) {
    out.push(check("structured data", "fail", "no JSON-LD blocks"));
  } else {
    out.push(check("structured data blocks", "pass", `${jsonLd.length} block(s)`));
    jsonLd.forEach((b, i) => {
      if (b.error) {
        out.push(check(`JSON-LD #${i + 1} valid`, "fail", b.error));
        return;
      }
      const arr = Array.isArray(b.data) ? b.data : [b.data];
      for (const item of arr) {
        if (!item || typeof item !== "object") {
          out.push(check(`JSON-LD #${i + 1}`, "fail", "not an object"));
          continue;
        }
        const obj = item as Record<string, unknown>;
        if (!obj["@context"]) out.push(check(`JSON-LD #${i + 1} @context`, "fail"));
        if (!obj["@type"]) out.push(check(`JSON-LD #${i + 1} @type`, "fail"));
      }
    });
  }

  return out;
}

function badgeColor(s: Severity) {
  if (s === "pass") return "bg-emerald-600";
  if (s === "warn") return "bg-amber-500";
  return "bg-red-600";
}

export function SeoAudit() {
  const location = useLocation();
  const [checks, setChecks] = useState<Check[] | null>(null);
  const [open, setOpen] = useState(false);

  const enabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (import.meta.env.DEV) return true;
    return new URLSearchParams(window.location.search).get("seo") === "1";
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setTimeout(() => setChecks(run()), SCAN_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [enabled, location.pathname, location.search]);

  if (!enabled || !checks) return null;

  const fails = checks.filter((c) => c.severity === "fail").length;
  const warns = checks.filter((c) => c.severity === "warn").length;
  const overall: Severity = fails ? "fail" : warns ? "warn" : "pass";

  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-sm font-mono text-xs text-foreground" data-testid="seo-audit">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-white shadow-lg ${badgeColor(overall)}`}
        aria-label="Toggle SEO audit panel"
      >
        <span>SEO</span>
        <span className="rounded bg-black/25 px-1.5">
          {overall === "pass" ? "OK" : `${fails} fail · ${warns} warn`}
        </span>
      </button>
      {open && (
        <div className="mt-2 max-h-[60vh] w-[360px] overflow-auto rounded-xl border border-border bg-card p-3 shadow-xl">
          <p className="mb-2 font-sans text-sm font-semibold text-foreground">
            SEO audit — {location.pathname}
          </p>
          <ul className="space-y-1.5">
            {checks.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${badgeColor(c.severity)}`} />
                <span className="flex-1 break-words">
                  <span className="font-semibold">{c.label}</span>
                  {c.detail ? <span className="ml-1 text-muted-foreground">— {c.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setChecks(run())}
            className="mt-3 w-full rounded-md border border-border px-2 py-1 text-foreground hover:bg-muted"
          >
            Re-scan
          </button>
        </div>
      )}
    </div>
  );
}
